import { useMemo } from 'react';
import { entityConfigs, withPersonLabels } from '@ast-data/shared';
import { useUserDirectory, type PersonLabels } from './userDirectory';

/** The entity a local collection holds (`finds` -> `find`), '' if none. */
export function entityNameOfCollection(collectionName: string): string {
    return (
        entityConfigs.find((entity) => entity.collection === collectionName)
            ?.name ?? ''
    );
}

export type { PersonLabels } from './userDirectory';

/**
 * People are shown by their ACCOUNT wherever a record links one: a name the
 * record stores as text ("K.Hecht", or a name the person has since changed)
 * gives way to the account's current name. Every place that shows records'
 * people takes them through here.
 *
 * Nothing is replaced until accounts and profiles have arrived: before that,
 * the label falls back to the Synology login name ("P.Eichelbrönner"), and a
 * form showing it would also SAVE it (see useUserLabelState). The stored text
 * shows instead. Worked out once, by the user directory (see userDirectory).
 */
export function usePersonLabels(): PersonLabels {
    return useUserDirectory().personLabels;
}

/**
 * A live query's documents as plain JSON with their people shown by their
 * accounts — the one line a list screen needs instead of mapping
 * `toMutableJSON()` itself (useProjectDocs and friends already do this).
 */
export function useShownJson<T extends object>(
    entityName: string,
    docs: readonly { toMutableJSON(): unknown }[]
): T[] {
    const personLabels = usePersonLabels();
    return useMemo(
        () =>
            docs.map((doc) =>
                personLabels.show(entityName, doc.toMutableJSON() as T)
            ),
        [docs, personLabels, entityName]
    );
}

/**
 * Documents an export loaded straight from a collection, as plain JSON with
 * their people shown by their accounts — for the code outside React that the
 * hooks above cannot reach. `labelOf` comes from usePersonLabels; without one,
 * the names stay as stored.
 */
export function showDocs<T>(
    collectionName: string,
    docs: readonly { toJSON(): unknown }[],
    labelOf?: (id: string) => string
): T[] {
    const entityName = entityNameOfCollection(collectionName);
    return docs.map((doc) => {
        const json = doc.toJSON() as T & object;
        return (
            labelOf ? withPersonLabels(entityName, json, labelOf) : json
        ) as T;
    });
}
